<template>
  <div class="animated fadeIn reg">
    <div class="kt-portlet kt-portlet--mobile" style="position:relative">
      <div class="kt-portlet__body kt-portlet__body--fit" ref="cnt">
        <div class="m-4">
          <div class="filters-container">
            <TableFilters
              v-model="filterOptions"
              style="margin-right: 45px"
              @click="refresh"
            />
            <DropdownColumns
              :fields="[]"
              class="customize"
              :export-link="exportLink"
              :export-json="exportJson"
            />
          </div>
          <div style="overflow:auto" class="mt-2">
            <b-table
              id="shop-balance-tx-table"
              :busy.sync="isBusy"
              show-empty
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :hover="true"
              :striped="true"
              class="table-wborder"
              v-model="copy"
              :items="provider"
              :fields="fields"
              :current-page="currentPage"
              :per-page="perPage"
              @row-clicked="rowClicked"
              tbody-tr-class="tr-pointer"
              v-columns-resizable="{ afterResize: columnsWidthChanged }"
            >
              <template v-slot:head()="scope">{{ $t(scope.label) }}</template>

              <template v-slot:cell(shop_code)="data">
                <span v-if="data.value">
                  <span v-if="data.value.to">
                    <router-link :to="`/shops/${data.value.to}`">{{
                      data.value.name
                    }}</router-link>
                  </span>
                  <span v-else>{{ data.value.name }}</span>
                </span>
              </template>

              <template v-slot:cell(shop_name)="data">
                <span v-if="data.value">
                  <span v-if="data.value.to">
                    <router-link :to="`/shops/${data.value.to}`">{{
                      data.value.name
                    }}</router-link>
                  </span>
                  <span v-else>{{ data.value.name }}</span>
                </span>
              </template>

              <template v-slot:cell(reference)="data">
                <span v-if="data.value">
                  <span v-if="data.value.to">
                    <router-link :to="`${data.value.to}`">{{
                      data.value.name
                    }}</router-link>
                  </span>
                  <span v-else>{{ data.value.name }}</span>
                </span>
              </template>

              <template v-slot:cell(timestamp)="data">
                <span>{{ mom_unix(data.value).format(tableDateLong) }}</span>
              </template>
              <template v-slot:cell(balance_timestamp)="data">
                <span>{{ mom_unix(data.value).format(tableDateShort) }}</span>
              </template>
              <template v-slot:cell(amount)="data">
                <span v-if="data.value">{{ roundThousands(data.value) }}</span>
                <span v-else>0.00</span>
              </template>
              <template v-slot:cell(amount_in)="data">
                <span v-if="data.value">{{ roundThousands(data.value) }}</span>
                <span v-else>0.00</span>
              </template>
              <template v-slot:cell(amount_out)="data">
                <span v-if="data.value">{{ roundThousands(data.value) }}</span>
                <span v-else>0.00</span>
              </template>
              <template v-slot:cell(unclaimed_winnings_amount)="data">
                <span v-if="data.value">{{ roundThousands(data.value) }}</span>
                <span v-else>0.00</span>
              </template>
              <template v-slot:cell(unclaimed_tickets_number)="data">
                <span v-if="data.value">{{ data.value }}</span>
                <span v-else>0</span>
              </template>
              <template v-slot:cell(type)="data">
                <span>{{ $t(data.value) }}</span>
              </template>

              <template v-slot:cell(action)="row">
                <b-button
                  size="sm"
                  @click="cancelTransaction(row.item)"
                  class="mr-1"
                  >{{ $t("Cancel") }}</b-button
                >
              </template>
            </b-table>
          </div>
          <div class="container-fluid">
            <div class="row">
              <div style="white-space:nowrap;" class="my-2">
                <PaginationSelect
                  v-model="perPage"
                  :options="paginationOpts"
                  variant="light"
                  size="sm"
                />
                <span>
                  &nbsp;
                  <i18n path="Showing {start}-{end} of {total} rows" tag="span">
                    <span slot="start">{{
                      (currentPage - 1) * perPage + 1
                    }}</span>
                    <span slot="end">{{ currentPage * perPage }}</span>
                    <span slot="total">{{ totalRows }}</span>
                  </i18n>
                </span>
                <b-button
                  size="sm"
                  @click.stop="
                    $root.$emit('bv::refresh::table', 'shop-balance-tx-table')
                  "
                  class="ml-2 refresh"
                  variant="light"
                  :title="$t('Refresh')"
                >
                  <i class="icon-refresh"></i>
                </b-button>
              </div>
              <nav class="ml-auto mt-2">
                <b-pagination
                  size="md"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  v-model="currentPage"
                />
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="bv-modal-add-transaction" hide-footer centered>
      <template slot="modal-title">{{ $t("Add Transaction") }}</template>
      <template slot="modal-header-close">
        <span />
      </template>
      <div class="d-block" style="position:relative">
        <b-form @submit.prevent="onAddTransaction" autocomplete="off" ref="l">
          <b-form-group :label="$t('Balance Timestamp')">
            <date-range-picker
              :startDate="details.startDate"
              :singleDatePicker="true"
              @update="updateDate"
              :locale-data="locale"
              opens="right"
              :ranges="false"
            />
          </b-form-group>
          <FloatingLabel
            v-model="details.amount_in"
            label="Amount In"
            type="number"
            step="0.01"
            autocomplete="false"
            size="sm"
          />
          <FloatingLabel
            v-model="details.amount_out"
            label="Amount Out"
            type="number"
            step="0.01"
            autocomplete="false"
            size="sm"
          />
          <FloatingLabel
            v-model="details.unclaimed_tickets"
            label="Unclaimed Tickets"
            type="number"
            autocomplete="false"
            size="sm"
          />
          <FloatingLabel
            v-model="details.unclaimed_winnings"
            label="Unclaimed Winnings"
            type="number"
            step="0.01"
            autocomplete="false"
            size="sm"
          />
          <div class="text-right">
            <b-button type="submit" variant="success" class="mt-3">{{
              $t("Add Transaction")
            }}</b-button>
          </div>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import startCase from "@/shared/strings";
import { successConf, errorConf } from "@/shared/notifications";
import tableMixin from "@/shared/mixins/table.mixin";
import PaginationSelect from "@/components/PaginationSelect";
import DropdownColumns from "@/components/DropdownColumns";
import TableFilters from "@/components/TableFilters";
import timeFormat from "@/shared/timeFormat";
import DateRangePicker from "@/components/calendar-range/DateRangePicker";
import FloatingLabel from "@/components/FloatingLabel";

export default {
  name: "shopBalanceTX",
  mixins: [tableMixin],
  components: {
    PaginationSelect,
    DropdownColumns,
    TableFilters,
    DateRangePicker,
    FloatingLabel
  },
  data: function() {
    return {
      tableName: "shopBalanceTX",
      filterOptions: [
        {
          field: "timestamp",
          type: "date",
          opens: "right",
          stateIgnore: true,
          selected: {
            startDate: moment().startOf("day"),
            endDate: moment().endOf("day")
          },
          default: {
            startDate: moment().startOf("day"),
            endDate: moment().endOf("day")
          },
          ranges: {
            "Last hour": [moment().subtract(1, "hour"), moment()],
            "Last 6 hours": [moment().subtract(6, "hours"), moment()],
            "Last 24 hours": [moment().subtract(24, "hours"), moment()],
            Today: [moment().startOf("day"), moment().endOf("day")],
            Yesterday: [
              moment()
                .subtract(1, "days")
                .startOf("day"),
              moment()
                .subtract(1, "days")
                .endOf("day")
            ],
            "This week": [
              moment().startOf("isoWeek"),
              moment().endOf("isoWeek")
            ],
            "Last week": [
              moment()
                .subtract(1, "week")
                .startOf("isoWeek"),
              moment()
                .subtract(1, "week")
                .endOf("isoWeek")
            ],
            "This month": [moment().startOf("month"), moment().endOf("month")],
            "Last month": [
              moment()
                .subtract(1, "month")
                .startOf("month"),
              moment()
                .subtract(1, "month")
                .endOf("month")
            ],
            "This year": [moment().startOf("year"), moment().endOf("year")],
            "Last year": [moment().subtract(1, "year"), moment()]
          }
        }
      ],
      isBusy: false,
      sortBy: "",
      sortDesc: true,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      filter: null,
      filterField: null,
      checkAll: false,
      copy: null,

      eid: "",

      addLabel: "Add Transaction",
      locale: {
        format: timeFormat.datePickerShort, //format of the dates displayed
        firstDay: 1, //ISO first day of week - see moment documenations for details
        showWeekNumbers: true //show week numbers on each row of the calendar
      },
      details: {
        startDate: moment(),
        amount_in: 0,
        amount_out: 0,
        unclaimed_tickets: 0,
        unclaimed_winnings: 0
      }
    };
  },
  computed: {
    fields() {
      const columns = [
        {
          key: "shop_code",
          label: "Shop Code",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "shop_name",
          label: "Shop Name",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "timestamp",
          label: "TX Timestamp",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "balance_timestamp",
          label: "Balance Timestamp",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "amount",
          label: "Amount",
          sortable: false,
          ignore: true,
          class: "text-right"
        },
        {
          key: "amount_in",
          label: "Amount In",
          sortable: false,
          ignore: true,
          class: "text-right"
        },
        {
          key: "amount_out",
          label: "Amount Out",
          sortable: false,
          ignore: true,
          class: "text-right"
        },
        {
          key: "type",
          label: "Type",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "reference",
          label: "Reference",
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "unclaimed_tickets_number",
          label: "# U. Tickets",
          headerTitle: this.$t("Unclaimed Tickets Number"),
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "unclaimed_winnings_amount",
          label: "# U. Winnings",
          headerTitle: this.$t("Unclaimed Winnings Amount"),
          sortable: false,
          ignore: true,
          class: ""
        },
        {
          key: "action",
          label: "Action",
          sortable: false,
          ignore: true
        }
      ];

      return columns;
    },
    lang() {
      return this.$store.getters["global/defLanguage"];
    },
    exportLink() {
      const apiUrl = this.$store.getters["global/apiUrl"];
      const endpoint = this.$store.getters["shopBalanceTX/endpoint"];

      return `${apiUrl}/${endpoint}?export=xls&lang=${this.lang}`;
    },
    exportJson() {
      const fields = [];
      this.fields.forEach(item => {
        if (
          typeof item.class != "undefined" &&
          item.class.indexOf("d-none") == -1
        ) {
          let key = item.key;
          if (key != "selected") {
            const efields = ["eshop_id"];

            if (efields.indexOf(key) > -1) {
              key = key.substr(1);
            }

            fields.push(key);
          }
        }
      });

      const paginationData = this.buildPagination();

      paginationData.fields = fields;

      return JSON.stringify(paginationData);
    }
  },
  beforeMount() {
    console.log(this.$route);
    if (this.$route.query.startDate && this.$route.query.startDate != "null") {
      this.filterOptions[0].selected.startDate = moment(
        this.$route.query.startDate
      );
    }
    if (this.$route.query.endDate && this.$route.query.endDate != "null") {
      this.filterOptions[0].selected.endDate = moment(
        this.$route.query.endDate
      );
    }
  },
  mounted() {
    this.eid = this.$route.params.id != "0" ? this.$route.params.id : "";

    if (this.selectedeid) {
      this.eid = this.selectedeid;
    } else if (!this.eid) {
      this.eid = "";
    }

    if (this.eid == "0" || this.eid == "") {
      this.$router.push("/shop-balance");
    }
  },
  methods: {
    cancelTransaction(item) {
      const loader = this.$loading.open({
        container: this.$refs.cnt
      });

      this.$store
        .dispatch("shopBalanceTX/delete", item.eid)
        .then(resp => {
          let msg = "Transaction Canceled";
          this.showSuccessNotification(msg);
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loader.close();
        });
    },

    redirect() {
      this.$bvModal.show("bv-modal-add-transaction");
    },

    updateDate(value) {
      this.details.startDate = value.startDate;
      // this.details.date_end = value.endDate;
    },

    onAddTransaction() {
      let detailsData = {
        shop_id: this.eid,
        balance_date: this.details.startDate,
        amount_in: parseInt(this.details.amount_in * 10000),
        amount_out: parseInt(this.details.amount_out * 10000),
        unclaimed_tickets: parseInt(this.details.unclaimed_tickets),
        unclaimed_winnings: parseInt(this.details.unclaimed_winnings * 10000)
      };

      // console.log("body", detailsData);

      const loadDet = this.$loading.open({
        container: this.$refs.l
      });
      let apiAction = "shopBalanceTX/create";
      this.$store
        .dispatch(apiAction, detailsData)
        .then(resp => {
          let msg = "Transaction added";
          this.showSuccessNotification(msg);
          this.$bvModal.hide("bv-modal-add-transaction");
          this.refresh();
        })
        .catch(err => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loadDet.close();
        });
    },

    refresh() {
      this.$root.$emit("bv::refresh::table", "shop-balance-tx-table");
    },
    provider(ctx, callback) {
      const loading = this.$loading.open({
        container: this.$refs.cnt
      });

      const paginationData = this.buildPagination();

      const shopByID = this.$store.getters["cache/shopByID"];

      Promise.all([
        this.$store.dispatch("cache/fetch", "shops").catch(e => e)
      ]).finally(() => {
        this.$store
          .dispatch("shopBalanceTX/fetch", paginationData)
          .then(resp => {
            this.totalRows = resp.data.total;
            resp.data.rows.forEach((item, index) => {
              if (item.eshop_id) {
                const shop = shopByID(item.eshop_id);
                if (shop) {
                  item.shop_name = {
                    to: item.eshop_id,
                    name: shop.name
                  };
                  item.shop_code = {
                    to: item.eshop_id,
                    name: shop.code
                  };
                } else {
                  item.shop_name = {
                    to: item.eshop_id,
                    name: item.eshop_id
                  };
                  item.shop_code = {
                    to: item.eshop_id,
                    name: item.eshop_id
                  };
                }
              }

              if (item.type && item.reference) {
                switch (item.type) {
                  case 1:
                  case 2:
                  case 3:
                  case 4:
                  case 5:
                  case 12:
                    item.reference = {
                      to: `/tickets/${item.reference}`,
                      name: item.reference
                    };
                    break;
                  default:
                    item.reference = {
                      to: null,
                      name: item.reference
                    };
                }
              }

              if (item.type) {
                switch (item.type) {
                  case 1:
                    item.type = "Ticket In";
                    break;
                  case 2:
                    item.type = "Ticket In Authorized";
                    break;
                  case 3:
                    item.type = "Ticket Canceled";
                    break;
                  case 4:
                    item.type = "Ticket Paid";
                    break;
                  case 5:
                    item.type = "Ticket Cross Paid";
                    break;
                  case 6:
                    item.type = "Cash Supply";
                    break;
                  case 7:
                    item.type = "Cash Deposit";
                    break;
                  case 8:
                    item.type = "Bank Supply";
                    break;
                  case 9:
                    item.type = "Bank Deposit";
                    break;
                  case 10:
                    item.type = "NSoft Merge";
                    break;
                  case 11:
                    item.type = "Newton Slots Merge";
                    break;
                  case 12:
                    item.type = "Ticket Invalidate Payment";
                    break;
                  case 13:
                    item.type = "Cash Supply Canceled";
                    break;
                  case 14:
                    item.type = "Cash Deposit Canceled";
                    break;
                  case 15:
                    item.type = "Bank Supply Canceled";
                    break;
                  case 16:
                    item.type = "Bank Deposit Canceled";
                    break;
                  case 17:
                    item.type = "WSix Merge";
                    break;
                  case 18:
                    item.type = "Manual Report";
                    break;
                }
              }
            });
            callback(resp.data.rows);
          })
          .catch(err => {
            this.showErrorNotification(err);
            this.totalRows = 0;
            callback([]);
          })
          .finally(() => {
            loading.close();
          });
      });
      // Must return null or undefined to signal b-table that callback is being used
      return null;
    },
    buildPagination() {
      const paginationData = {
        rowsPerPage: this.perPage,
        page: this.currentPage
      };

      if (this.filter && this.filterField) {
        paginationData.likeValues = [this.filter];
        paginationData.likeFields = [this.filterField];
      } else {
        paginationData.likeValues = [];
        paginationData.likeFields = [];
      }

      if (this.sortBy) {
        paginationData.sortBy = [this.sortBy];
        paginationData.sortDirection = [this.sortDesc ? "DESC" : "ASC"];
      }

      const exactFields = ["shop_id"];
      const exactValues = [[this.eid]];

      this.filterOptions.forEach(item => {
        if (item.type != "date" && item.selected.length) {
          exactFields.push(item.field);
          if (item.trackBy) {
            const tmp = [];
            item.selected.forEach(el => {
              tmp.push(el[item.trackBy]);
            });
            exactValues.push(tmp);
          } else {
            exactValues.push(item.selected);
          }
        } else if (item.type == "date") {
          if (item.selected.startDate && item.selected.endDate) {
            paginationData.dateField = item.field;
            paginationData.dateStart = item.selected.startDate;
            paginationData.dateEnd = item.selected.endDate;
          } else {
            paginationData.dateField = item.field;
            paginationData.dateStart = moment().startOf("month");
            paginationData.dateEnd = moment().endOf("month");
          }
        }
      });

      if (exactFields.length) {
        paginationData.exactFields = exactFields;
        paginationData.exactValues = exactValues;
      }

      return paginationData;
    },
    rowClicked(item, index, event) {},
    showErrorNotification(err) {
      if (err.details && err.details.length) {
        this.$bvToast.toast(
          this.$t(err.details[0]),
          errorConf(this.$t("Error"))
        );
      } else {
        this.$bvToast.toast(this.$t(err.message), errorConf(this.$t("Error")));
      }
    },
    showSuccessNotification(msg) {
      this.$bvToast.toast(this.$t(msg), successConf(this.$t("Success")));
    }
  }
};
</script>

<style lang="scss" scoped>
.reg {
  & /deep/ #cash-register-table tfoot th {
    border: 0 !important;
  }
}
</style>
